import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ConnexionsTab, { PAGE_SIZE as CONNEXIONS_PAGE_SIZE } from "./ConnexionsTab.js";
import LmTab, { PAGE_SIZE as LM_PAGE_SIZE, LM_ACTIONS } from "./LmTab.js";
import EvenementTab, {
  PAGE_SIZE as EVENEMENT_PAGE_SIZE,
  EVENEMENT_ACTIONS,
} from "./EvenementTab.js";
import ClientTab, {
  PAGE_SIZE as CLIENT_PAGE_SIZE,
  CLIENT_ACTIONS,
} from "./ClientTab.js";
import AnnuaireTab, {
  PAGE_SIZE as ANNUAIRE_PAGE_SIZE,
  ANNUAIRE_ACTIONS,
} from "./AnnuaireTab.js";
import TodoTab, { PAGE_SIZE as TODO_PAGE_SIZE, TODO_ACTIONS } from "./TodoTab.js";
import ProspectTab, {
  PAGE_SIZE as PROSPECT_PAGE_SIZE,
  PROSPECT_ACTIONS,
} from "./ProspectTab.js";
import MandatTab, { PAGE_SIZE as MANDAT_PAGE_SIZE, MANDAT_ACTIONS } from "./MandatTab.js";
import SuiviProdTab, {
  PAGE_SIZE as SUIVI_PROD_PAGE_SIZE,
  SUIVI_PROD_ACTIONS,
} from "./SuiviProdTab.js";
import useCategorySummary from "./useCategorySummary.js";

// Écran Super Admin : une vue d'ensemble (dashboard) sert de page d'accueil,
// avec une carte par catégorie (total + barre de progression relative). En
// cliquant sur une carte, on bascule sur le détail (tableau complet) de
// cette catégorie. Chaque catégorie est activée phase par phase (Connexions,
// Todo, FormLead, LM, Événements, Client, Annuaire, Suivi de prod, Mandat).
const CATEGORIES = [
  { key: "connexions", label: "Connexions" },
  { key: "todo", label: "Todo" },
  { key: "formlead", label: "Prospect" },
  { key: "lm", label: "Lettres de mission" },
  { key: "evenement", label: "Événements" },
  { key: "client", label: "Client" },
  { key: "annuaire", label: "Annuaire" },
  { key: "suiviProd", label: "Suivi de prod" },
  { key: "mandat", label: "Mandat" },
];

// Filtre de date global (partagé par la vue d'ensemble et tous les détails) :
// uniquement des préréglages, pas de plage libre — cf. demande explicite.
const DATE_PRESETS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "yesterday", label: "Hier" },
  { key: "week", label: "Cette semaine" },
  { key: "month", label: "Ce mois" },
];

const pad = (n) => String(n).padStart(2, "0");
const toDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Lundi comme premier jour de semaine (convention FR).
function getPresetRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "today":
      return { from: toDateKey(today), to: toDateKey(today) };
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: toDateKey(yesterday), to: toDateKey(yesterday) };
    }
    case "week": {
      const day = today.getDay(); // 0 = dimanche
      const diffToMonday = day === 0 ? 6 : day - 1;
      const monday = new Date(today);
      monday.setDate(monday.getDate() - diffToMonday);
      return { from: toDateKey(monday), to: toDateKey(today) };
    }
    case "month": {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toDateKey(firstOfMonth), to: toDateKey(today) };
    }
    default:
      return { from: "", to: "" };
  }
}

function SuperAdmin() {
  const [view, setView] = useState("overview"); // "overview" | "detail"
  const [activeCategory, setActiveCategory] = useState(null);
  const [datePreset, setDatePreset] = useState("today");

  const { from, to } = useMemo(() => getPresetRange(datePreset), [datePreset]);

  const connexions = useCategorySummary({
    endpoint: "login-logs",
    pageSize: CONNEXIONS_PAGE_SIZE,
    from,
    to,
  });
  const lm = useCategorySummary({
    endpoint: "audit-logs",
    action: LM_ACTIONS,
    pageSize: LM_PAGE_SIZE,
    from,
    to,
  });
  const evenement = useCategorySummary({
    endpoint: "audit-logs",
    action: EVENEMENT_ACTIONS,
    pageSize: EVENEMENT_PAGE_SIZE,
    from,
    to,
  });
  const client = useCategorySummary({
    endpoint: "audit-logs",
    action: CLIENT_ACTIONS,
    pageSize: CLIENT_PAGE_SIZE,
    from,
    to,
  });
  const annuaire = useCategorySummary({
    endpoint: "audit-logs",
    action: ANNUAIRE_ACTIONS,
    pageSize: ANNUAIRE_PAGE_SIZE,
    from,
    to,
  });
  const todo = useCategorySummary({
    endpoint: "audit-logs",
    action: TODO_ACTIONS,
    pageSize: TODO_PAGE_SIZE,
    from,
    to,
  });
  const prospect = useCategorySummary({
    endpoint: "audit-logs",
    action: PROSPECT_ACTIONS,
    pageSize: PROSPECT_PAGE_SIZE,
    from,
    to,
  });
  const mandat = useCategorySummary({
    endpoint: "audit-logs",
    action: MANDAT_ACTIONS,
    pageSize: MANDAT_PAGE_SIZE,
    from,
    to,
  });
  const suiviProd = useCategorySummary({
    endpoint: "audit-logs",
    action: SUIVI_PROD_ACTIONS,
    pageSize: SUIVI_PROD_PAGE_SIZE,
    from,
    to,
  });

  const totalsByCategory = {
    connexions: connexions.data?.total ?? null,
    lm: lm.data?.total ?? null,
    evenement: evenement.data?.total ?? null,
    client: client.data?.total ?? null,
    annuaire: annuaire.data?.total ?? null,
    todo: todo.data?.total ?? null,
    formlead: prospect.data?.total ?? null,
    mandat: mandat.data?.total ?? null,
    suiviProd: suiviProd.data?.total ?? null,
  };
  const loadingByCategory = {
    connexions: connexions.loading,
    lm: lm.loading,
    evenement: evenement.loading,
    client: client.loading,
    annuaire: annuaire.loading,
    todo: todo.loading,
    formlead: prospect.loading,
    mandat: mandat.loading,
    suiviProd: suiviProd.loading,
  };
  const maxTotal = Math.max(1, ...Object.values(totalsByCategory).filter((v) => v != null));

  const handleOpenCategory = (key) => {
    setActiveCategory(key);
    setView("detail");
  };

  const activeCategoryLabel = CATEGORIES.find((c) => c.key === activeCategory)?.label;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          {view === "detail" ? (
            <button
              onClick={() => setView("overview")}
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-[#840040] transition-colors mb-1"
            >
              <ChevronLeft size={16} />
              Vue d'ensemble
            </button>
          ) : (
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Super Admin
            </h1>
          )}
          <p className="text-sm text-gray-400">
            {view === "detail"
              ? activeCategoryLabel
              : "Connexions et activité de la plateforme, tous utilisateurs confondus"}
          </p>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap p-1.5 bg-gray-100 rounded-xl w-fit">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={() => setDatePreset(preset.key)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                datePreset === preset.key
                  ? "bg-[#840040] text-white"
                  : "text-gray-600 hover:bg-white"
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {view === "overview" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map((cat) => {
            const total = totalsByCategory[cat.key];
            const isLoading = loadingByCategory[cat.key];
            const hasData = total != null;
            const pct = hasData ? Math.min(100, (total / maxTotal) * 100) : 0;

            return (
              <button
                key={cat.key}
                onClick={() => handleOpenCategory(cat.key)}
                className="text-left bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md hover:border-[#840040]/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {cat.label}
                  </span>
                  <ChevronRight
                    size={16}
                    className="text-gray-300 group-hover:text-[#840040] transition-colors"
                  />
                </div>

                {isLoading ? (
                  <div className="h-8 w-14 bg-gray-100 rounded animate-pulse mb-3" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900 mb-3">
                    {hasData ? total : "—"}
                  </p>
                )}

                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      isLoading
                        ? "bg-gray-200 animate-pulse w-2/5"
                        : hasData
                        ? "bg-[#840040] transition-all"
                        : "bg-gray-200"
                    }`}
                    style={!isLoading ? { width: `${pct}%` } : undefined}
                  />
                </div>

                {!hasData && !isLoading && (
                  <p className="text-[10px] text-gray-400 mt-2">
                    Bientôt disponible
                  </p>
                )}
              </button>
            );
          })}
        </div>
      ) : activeCategory === "connexions" ? (
        <ConnexionsTab from={from} to={to} initialData={connexions.data} />
      ) : activeCategory === "lm" ? (
        <LmTab from={from} to={to} initialData={lm.data} />
      ) : activeCategory === "evenement" ? (
        <EvenementTab from={from} to={to} initialData={evenement.data} />
      ) : activeCategory === "client" ? (
        <ClientTab from={from} to={to} initialData={client.data} />
      ) : activeCategory === "annuaire" ? (
        <AnnuaireTab from={from} to={to} initialData={annuaire.data} />
      ) : activeCategory === "todo" ? (
        <TodoTab from={from} to={to} initialData={todo.data} />
      ) : activeCategory === "formlead" ? (
        <ProspectTab from={from} to={to} initialData={prospect.data} />
      ) : activeCategory === "mandat" ? (
        <MandatTab from={from} to={to} initialData={mandat.data} />
      ) : activeCategory === "suiviProd" ? (
        <SuiviProdTab from={from} to={to} initialData={suiviProd.data} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center gap-2 min-h-[240px]">
          <p className="text-sm font-semibold text-gray-700">
            {activeCategoryLabel} — bientôt disponible
          </p>
          <p className="text-xs text-gray-400 max-w-sm">
            Cette catégorie sera activée au fur et à mesure de la mise en
            place du suivi d'activité de la plateforme.
          </p>
        </div>
      )}
    </div>
  );
}

export default SuperAdmin;
